import axios from 'axios';
import {
  CountReportRows,
  DownloadExecutedReport,
  ReportFilters,
  SaveReportNotificationSettings,
  UpdateReportNotificationSettings,
} from 'models/request';
import { trackPromise } from 'react-promise-tracker';
import {
  ExecReport,
  GetInfoForQuery,
  GetReports,
  DownloadedReport,
  INotificationReportSettings,
} from 'models/response';
import { ExecReportPayload } from 'models/request';
import { REPORTS } from 'utils/constants';

/**
 *  Get reports for dropdown selection
 * @param {number} idUser
 * @return {Promise<AxiosResponse<GetReports>>}
 */
const getReports = (idUser: number) =>
  trackPromise(axios.post<GetReports>(`${REPORTS}/getReports?idUser=${idUser}`), 'module');

/**
 *  Get reports list executed
 * @param {number} idUser
 * @return {Promise<AxiosResponse<DownloadedReport[]>>}
 */
const getReportListExecuted = (idUser: number) =>
  trackPromise(axios.get<DownloadedReport[]>(`${REPORTS}/getReportListExecuted?idUser=${idUser}`), 'module');

const getInfoForQuery = (idReport: number, idUser: number | undefined, idProgram: number) =>
  trackPromise(
    axios.get<GetInfoForQuery>(
      `${REPORTS}/getInfoForQuery?idReport=${idReport}&idUser=${idUser}&idProgram=${idProgram}`,
    ),
    'module',
  );

/**
 *  Execute a report
 *  @param {idReport} number
    @param {idUser} number
    @param {queryReportInfo} GetInfoForQuery
    @param {outputFormat} string
    @param {async} boolean
 * @return {Promise<ExecReport | SimpleError>>}
 */

const execReport = (payload: ExecReportPayload) =>
  trackPromise(axios.post<ExecReport>(`${REPORTS}/execReport`, payload), 'modal');

/**
 * Download executed record
 * @param {Object} body
 * @param {number} body.idUser
 * @param {string} body.pathToFile
 * @return {Promise<AxiosResponse<string>>}
 */
const downloadExecutedReport = (body: DownloadExecutedReport) =>
  trackPromise(axios.post<string>(`${REPORTS}/downloadExecutedReport`, body), 'module');

/**
 * Execute a report for KPI
 * @param {body} body filters
 * @return {Promise<AxiosResponse<any>>}
 */
const execKpiReport = (body: ReportFilters) =>
  trackPromise(axios.post<ExecReport>(`${REPORTS}/execKpiReport`, body), 'module');

/**
 * Calculate total rows
 * @param {Object} data
 * @param {string} data.table
 * @param {string} body.source
 * @param {{columnHeader: string, columnName: string}[]} body.selectItems
 * @param {ReportFilter[]} filters
 * @return {Promise<AxiosResponse<number>>}
 */
const calculateRows = (data: CountReportRows) => {
  return trackPromise(axios.post(`${REPORTS}/countReportRows`, data), 'module');
};

const getScheduledNotificationsByUser = (idUser: number) => {
  return trackPromise(
    axios.get<INotificationReportSettings[]>(`${REPORTS}/getScheduledNotificationsByUser?idUser=${idUser}`),
    'module',
  );
};

// REPORT
/**
 * Create new user-notification report
 * @param {Object} body
 * @param {number} body.idUser
 * @param {number} body.idReport
 * @param {boolean} body.enabled
 * @param {string} body.cronFrequency
 * @return {Promise<AxiosResponse>}
 */
const saveScheduledReport = (body: SaveReportNotificationSettings) => {
  return trackPromise(axios.post<{ idSetting: number }>(`${REPORTS}/saveOrUpdateScheduledReport`, body), 'module');
};

/**
 * Edit user-notification report
 * @param {Object} body
 * @param {number} body.idUser
 * @param {number} body.idReport
 * @param {boolean} body.enabled
 * @param {string} body.cronFrequency
 * @param {number} body.idSetting
 * @return {Promise<AxiosResponse>}
 */
const editScheduledReport = (body: UpdateReportNotificationSettings) => {
  return trackPromise(axios.post<{ idSetting: number }>(`${REPORTS}/saveOrUpdateScheduledReport`, body), 'module');
};

const deleteScheduledNotificationSetting = (idSetting: number) =>
  trackPromise(axios.post(`${REPORTS}/deleteScheduledNotificationSetting?idSetting=${idSetting}`), 'modal');

export default {
  getReports,
  getInfoForQuery,
  getReportListExecuted,
  execReport,
  downloadExecutedReport,
  calculateRows,
  execKpiReport,
  getScheduledNotificationsByUser,
  saveScheduledReport,
  editScheduledReport,
  deleteScheduledNotificationSetting,
};
